import {SurveyService} from '../../services/survey.service';
import {ProcessParticipationAnswer} from '../../models/process-models';
import {Observable} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {ProcessParticipationService} from '../../services/process.participation.service';
import {Component, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import * as moment from 'moment';
import {
    Survey,
    LocalizedQuestionCategory,
    SurveyInfrastructure,
    QuestionType,
    CompositeAnswerPortion
} from '../../models/questions';
import {LanguageService} from '../../services/language.service';
import {concatMap, tap} from 'rxjs/operators';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';
import {ConfigService} from '../../services/config.service';
import {SurveyFilter} from '../survey-list/survey.filter.model';
import {ThankYouModalComponent} from "../../common/modal/thank-you-modal/thank-you-modal/thank-you-modal.component";

@Component({
    selector: 'app-survey',
    templateUrl: './survey.component.html',
    styleUrls: ['./survey.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class SurveyComponent implements OnInit {
    @ViewChild('thankYouModal') thankYouModal: ThankYouModalComponent;
    isPassing: boolean = false;
    answers$: Observable<Array<ProcessParticipationAnswer>>;
    survey$: Observable<Survey>;
    categoriesWithQuestions: Array<LocalizedQuestionCategory> = [];
    processParticipationId: number;
    isSelfAssessmentTab: boolean;


    private lastFreeFormAnswer: ProcessParticipationAnswer;
    private notificationKey: string = 'validation.toasterNotifications';
    private readonly decimalScale = 10;
    private toasterErrorNotificationHeader: string;
    private toasterErrorNotificationBody: string;
    private toasterErrorSubmitHeader: string;
    private toasterErrorSubmitBody: string;

    constructor(private readonly processParticipationService: ProcessParticipationService,
                private readonly route: ActivatedRoute, private readonly router: Router,
                private readonly languageService: LanguageService, private readonly toastr: ToastrService,
                private readonly translate: TranslateService, private readonly configService: ConfigService,
                private readonly surveyService: SurveyService) {
    }

    ngOnInit() {
        this.processParticipationId = +this.route.snapshot.paramMap.get('id');
        this.route.paramMap
            .subscribe(params => {
                this.isSelfAssessmentTab = parseInt(params.get('currentFilter'), this.decimalScale) === SurveyFilter.ByAppraisee ?
                    true : false;
            });

        this.getSurvey(this.processParticipationId);
        this.changeLanguage(this.processParticipationId);
        this.notificationKey = this.configService
            .configData.surveyToasterUncopleteNotificationsPath;
        this.localizedToasterNames();
    }

    getLocalizedDate(date): string {
        return moment(date).format('DD.MM.YYYY');
    }

    completeSurvey(processId: number) {
        if (!this.categoriesWithQuestions.every(ct => this.getIsAllMandatoryAnswered(ct))) {
            this.toastr.error(this.toasterErrorNotificationBody, this.toasterErrorNotificationHeader);
            return;
        }

        this.processParticipationService.completeProcessParticipation(this.processParticipationId, processId)
            .subscribe(() => {
                    this.thankYouModal.onShow();
                    this.thankYouModal.modalRef.onHide
                        .subscribe(() => this.router.navigate(['/user/survey-list']));
                },
                () => {
                    this.toastr.error(this.toasterErrorSubmitBody, this.toasterErrorSubmitHeader);
                }
            )

    }


    onQuestionAnswered(question: SurveyInfrastructure) {
        const categoryQuestion = this.categoriesWithQuestions.flatMap(x => x.questions).find(x => x.id === question.id);
        categoryQuestion.processParticipationAnswers = question.processParticipationAnswers;
        this.categoriesWithQuestions.forEach(x => this.setCategoryResponded(x));
    }

    private setCategoryResponded(category: LocalizedQuestionCategory) {
        category.isCategoryResponded = this.getIsAllMandatoryAnswered(category);
    }

    private localizedToasterNames() {
        this.translate
            .get(this.notificationKey + '.uncompletedSurvey')
            .subscribe(x => {
                this.toasterErrorNotificationHeader = x;
            });
        this.translate
            .get(this.notificationKey + '.uncompletedSurveyDetail')
            .subscribe(x => {
                this.toasterErrorNotificationBody = x;
            });
        this.translate
            .get('error.errorSubmit.head')
            .subscribe(x => {
                this.toasterErrorSubmitHeader = x;
            });
        this.translate
            .get('error.errorSubmit.body')
            .subscribe(x => {
                this.toasterErrorSubmitBody = x;
            });
    }

    private getIsQuestionResponded(question: SurveyInfrastructure): boolean {
        if (this.getIsMatrixQuestion(question)) {
            const answerIds: Array<number> = question.processParticipationAnswers.map(x => JSON.parse(x.customValue)).map(x => x.row);
            const rows = question.answers.filter(x => x.compositeAnswerPortion === CompositeAnswerPortion.Row);
            return rows.every(a => answerIds.includes(a.id));
        }

        return question.processParticipationAnswers.length > 0 && this.getIsValidAnswer(question);
    }

    private getIsAllMandatoryAnswered(questionCategory: LocalizedQuestionCategory): boolean {
        const allMandatoryQuestion = questionCategory.questions.filter(x => x.isMandatory);
        return allMandatoryQuestion.every(q => this.getIsQuestionResponded(q));
    }

    private getIsValidAnswer(question: SurveyInfrastructure): boolean {
        return question.answerType === QuestionType.Text || question.answerType === QuestionType.Number
            ? !!(question.processParticipationAnswers[0].customValue || '').length
            : question.processParticipationAnswers.some(x => !!x.answerId);
    }

    private sortQuestionByOrder(surveyInfrastructures: Array<SurveyInfrastructure>) {
        surveyInfrastructures.sort((a, b) => a.order - b.order);
    }

    private groupQuestionByCategory(surveyQuestions: Array<SurveyInfrastructure>,
                                    processParticipationId: number): Array<LocalizedQuestionCategory> {
        const localizedQuestionCategories: Array<LocalizedQuestionCategory> = [];
        this.sortQuestionByOrder(surveyQuestions);
        const surveyCategoriesId = Array.from(new Set(surveyQuestions.map(x => x.categoryId)));
        surveyCategoriesId.forEach(x => {
            const questionsInCategory = surveyQuestions.filter(q => q.categoryId === x);
            localizedQuestionCategories.push({
                id: x, name: [...questionsInCategory].shift().categoryName,
                questions: questionsInCategory
            });
        });
        return localizedQuestionCategories;
    }

    private getSurvey(processParticipationId: number) {
        this.localizedToasterNames();
        this.answers$ = this.processParticipationService.getSurveyAnswers(this.processParticipationId);
        const survey$ = this.processParticipationService.getSurveyByProcessId(this.processParticipationId);
        this.survey$ = this.answers$.pipe(
            concatMap(answers => survey$
                .pipe(tap(x => {
                    x.infrastructure.forEach(q => {
                        q.processParticipationId = this.processParticipationId;
                        q.processParticipationAnswers = answers.filter(a => a.surveyInfrastructureId === q.id);
                    });
                    this.categoriesWithQuestions = this.groupQuestionByCategory(x.infrastructure, this.processParticipationId);
                    this.categoriesWithQuestions.forEach(x => this.setCategoryResponded(x));
                    if (this.isSelfAssessmentTab) {
                        this.surveyService.selfAssessmentSurveyOpen(x.process.name);
                    }
                })))
        );
    }

    private changeLanguage(processParticipationId: number) {
        this.languageService.languageChanged$
            .subscribe(selectedLanguage => {
                this.getSurvey(processParticipationId);
            });
    }

    private getIsMatrixQuestion(question: SurveyInfrastructure): boolean {
        return question.answerType === QuestionType.MatrixCheckbox || question.answerType === QuestionType.MatrixRadiobutton;
    }
}
